import { useMemo } from "react";

import { OrganizationRoles, Roles } from "@/shared/utils/constants";
import { OrganizationPermissions } from "@/features/organizations/hooks/types";
import { BaseOrganizationResponse } from "@/redux/organizations/types";
import { FullUserResponse } from "@/redux/users/types";

export const useOrganizationPermissions = (
  currentUser: FullUserResponse | null | undefined,
  organization: BaseOrganizationResponse | null | undefined,
): OrganizationPermissions => {
  return useMemo(() => {
    if (!currentUser) {
      return {
        canEdit: false,
        canDelete: false,
        canCreate: false,
        canImport: false,
        canViewOverviewTab: false,
        canViewInvitesTab: false,
        canViewMembersTab: false,
        canViewEvidenceTab: false,
        canViewReportsTab: false,
        canViewSettingsTab: false,
        canViewApplicationsTab: false,
        canManageDomains: false,
        canExportData: false,
      };
    }

    const isMemberOfOrganization =
      currentUser.organization?.id == organization?.id;
    const isAdmin =
      currentUser.organizationRole === OrganizationRoles.ADMIN.value;
    const isSupervisor =
      currentUser.organizationRole === OrganizationRoles.SUPERVISOR.value;
    const isGuest =
      currentUser.organizationRole === OrganizationRoles.GUEST.value;

    const isAppAdmin = currentUser.role === Roles.ADMIN.value;

    return {
      canEdit:
        (isMemberOfOrganization && (isAdmin || isSupervisor)) || isAppAdmin,
      canDelete: isAppAdmin || isAdmin,
      canCreate: isAppAdmin,
      canImport: isAppAdmin,
      canViewOverviewTab: true,
      canViewInvitesTab:
        isAppAdmin || (isMemberOfOrganization && (isAdmin || isSupervisor)),
      canViewMembersTab: !isGuest,
      canViewEvidenceTab: !isGuest,
      canViewReportsTab: true,
      canViewSettingsTab:
        isAppAdmin || (isMemberOfOrganization && (isAdmin || isSupervisor)),
      canViewApplicationsTab:
        isAppAdmin || (isMemberOfOrganization && (isAdmin || isSupervisor)),
      canManageDomains:
        isAppAdmin || (isMemberOfOrganization && (isAdmin || isSupervisor)),
      canExportData: isAppAdmin || (isMemberOfOrganization && isAdmin),
    };
  }, [currentUser]);
};
